import React, { useState, useEffect } from 'react';
import { X, Camera, Plus, Trash2 } from 'lucide-react';
import { Button } from '@common/Button';
import { Input, TextArea, Select } from '@common/FormFields';
import { POI_TYPES } from '../../../../constants/poiTypes';
import { uploadFile } from '@utils/fileHelpers';
import { logError, logInfo } from '@utils/logger';
import { Toast } from '@common/Toast';

export const POIForm = ({ poi = null, isEdit = false, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    type: '',
    notes: '',
    measurements: [],
    photos: [],
    coordinates: poi?.coordinates || null,
    address: poi?.address || ''
  });
  const [newPhotos, setNewPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);

  // Initialize form data if editing an existing POI
  useEffect(() => {
    if (isEdit && poi) {
      setFormData({
        type: poi.type || '',
        notes: poi.notes || '',
        measurements: poi.measurements || [],
        photos: poi.photos || [],
        coordinates: poi.coordinates || null,
        address: poi.address || ''
      });
    }
  }, [isEdit, poi]);

  const handlePhotoUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setLoading(true);
    setError('');

    try {
      // Store files for preview
      setNewPhotos(prev => [...prev, ...files]);
      
      setToast({
        type: 'success',
        message: `${files.length} photo(s) selected`
      });
    } catch (error) {
      logError('Failed to process photos:', error);
      setError('Failed to process photos');
      setToast({
        type: 'error',
        message: 'Failed to process photos'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate required fields
      if (!formData.type) {
        throw new Error('POI type is required');
      }

      // Upload new photos if any
      const uploadedPhotoUrls = [];
      if (newPhotos.length > 0) {
        for (const photo of newPhotos) {
          try {
            const url = await uploadFile(photo, 'poi-photos');
            uploadedPhotoUrls.push(url);
          } catch (error) {
            logError('Failed to upload photo:', error);
            // Continue with other photos
          }
        }
      }

      // Combine existing and new photos
      const allPhotos = [...formData.photos, ...uploadedPhotoUrls];

      // Prepare POI data
      const poiData = {
        ...formData,
        photos: allPhotos,
        updatedAt: new Date().toISOString()
      };

      // Call onSave with the POI data
      await onSave(poiData);
      
      logInfo('POI saved successfully');
    } catch (error) {
      logError('Failed to save POI:', error);
      setError(error.message || 'Failed to save POI');
      setToast({
        type: 'error',
        message: error.message || 'Failed to save POI'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[400] p-4 overflow-y-auto">
      <div className="bg-[#2A2B2E] rounded-lg w-full max-w-xl my-8">
        <div className="flex justify-between items-center p-6 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">
            {isEdit ? 'Edit POI' : 'Add New POI'}
          </h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-lg"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-500/10 text-red-400 p-4 rounded-lg">
              {error}
            </div>
          )}

          {/* POI Type */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              POI Type
            </label>
            <Select
              value={formData.type}
              onChange={(value) => setFormData({ ...formData, type: value })}
              options={Object.keys(POI_TYPES).map(type => ({ value: type, label: type }))}
              required
              className="bg-gray-700 border-gray-600 text-white"
              labelClassName="text-gray-300"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Notes
            </label>
            <TextArea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={4}
              className="bg-gray-700 border-gray-600 text-white"
              labelClassName="text-gray-300"
            />
          </div>

          {/* Measurements */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Measurements
            </label>
            <div className="space-y-2">
              {formData.measurements.map((measurement, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    value={measurement.value}
                    onChange={(e) => {
                      const updatedMeasurements = [...formData.measurements];
                      updatedMeasurements[index].value = e.target.value;
                      setFormData({ ...formData, measurements: updatedMeasurements });
                    }}
                    className="bg-gray-700 border-gray-600 text-white flex-1"
                  />
                  <Select
                    value={measurement.unit}
                    onChange={(value) => {
                      const updatedMeasurements = [...formData.measurements];
                      updatedMeasurements[index].unit = value;
                      setFormData({ ...formData, measurements: updatedMeasurements });
                    }}
                    options={[
                      { value: 'cm', label: 'cm' },
                      { value: 'm', label: 'm' },
                      { value: 'in', label: 'in' },
                      { value: 'ft', label: 'ft' }
                    ]}
                    className="bg-gray-700 border-gray-600 text-white w-24"
                  />
                  <Button
                    type="button"
                    onClick={() => {
                      const updatedMeasurements = [...formData.measurements];
                      updatedMeasurements.splice(index, 1);
                      setFormData({ ...formData, measurements: updatedMeasurements });
                    }}
                    className="p-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                onClick={() => {
                  setFormData({
                    ...formData,
                    measurements: [
                      ...formData.measurements,
                      { value: '', unit: 'm' }
                    ]
                  });
                }}
                className="w-full bg-gray-700 hover:bg-gray-600 text-white"
              >
                Add Measurement
              </Button>
            </div>
          </div>

          {/* Photos */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Photos
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = 'image/*';
                  input.capture = 'environment';
                  input.onchange = handlePhotoUpload;
                  input.click();
                }}
                className="aspect-square flex flex-col items-center justify-center bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-600"
              >
                <Camera className="w-8 h-8 text-gray-400" />
                <span className="text-sm text-gray-400">Take Photo</span>
              </button>
              <label className="aspect-square flex flex-col items-center justify-center bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-600">
                <Plus className="w-8 h-8 text-gray-400" />
                <span className="text-sm text-gray-400">Add Photo</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handlePhotoUpload}
                  disabled={loading}
                />
              </label>
            </div>
            
            {/* Existing Photos */}
            {formData.photos.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-medium text-gray-300 mb-2">Existing Photos</h3>
                <div className="grid grid-cols-3 gap-2">
                  {formData.photos.map((photo, index) => (
                    <div key={index} className="relative">
                      <img
                        src={photo}
                        alt={`Photo ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const updatedPhotos = [...formData.photos];
                          updatedPhotos.splice(index, 1);
                          setFormData({ ...formData, photos: updatedPhotos });
                        }}
                        className="absolute top-1 right-1 p-1 bg-red-500 rounded-full text-white"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* New Photos Preview */}
            {newPhotos.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-medium text-gray-300 mb-2">New Photos</h3>
                <div className="grid grid-cols-3 gap-2">
                  {newPhotos.map((photo, index) => (
                    <div key={index} className="relative">
                      <img
                        src={URL.createObjectURL(photo)}
                        alt={`New Photo ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const updatedPhotos = [...newPhotos];
                          updatedPhotos.splice(index, 1);
                          setNewPhotos(updatedPhotos);
                        }}
                        className="absolute top-1 right-1 p-1 bg-red-500 rounded-full text-white"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              onClick={onClose}
              className="bg-gray-700 hover:bg-gray-600 text-white"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isEdit ? 'Update POI' : 'Save POI'}
            </Button>
          </div>
        </form>
      </div>

      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};